import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for listening to real-time notifications via SSE
 * Usage: const { addListener, removeListener } = useNotifications();
 */
export const useNotifications = () => {
  const eventSourceRef = useRef(null);
  const listenersRef = useRef([]);
  const reconnectTimeoutRef = useRef(null);

  const setupSSE = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    try {
      const streamUrl = `/api/notifications/stream?token=${token}`;
      eventSourceRef.current = new EventSource(streamUrl);

      eventSourceRef.current.addEventListener('open', () => {
        // Connection established silently
      });

      eventSourceRef.current.addEventListener('message', (event) => {
        try {
          const newNotification = JSON.parse(event.data);

          // Only log if it's a real notification (not keep-alive)
          if (newNotification.type) {
            console.log('[SSE] 📬 Received notification:', newNotification.type);
          }

          // Call all registered listeners
          if (listenersRef.current.length > 0) {
            listenersRef.current.forEach((listener) => {
              try {
                listener(newNotification);
              } catch (err) {
                console.error('[SSE] Error in listener:', err);
              }
            });
          }
        } catch (err) {
          // Ignore keep-alive messages
        }
      });

      eventSourceRef.current.addEventListener('error', () => {
        eventSourceRef.current?.close();
        // Attempt reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          setupSSE();
        }, 5000);
      });
    } catch (err) {
      console.error('[SSE] Failed to setup stream:', err);
    }
  }, []);

  const addListener = useCallback((listener) => {
    listenersRef.current.push(listener);
    return () => {
      listenersRef.current = listenersRef.current.filter(l => l !== listener);
    };
  }, []);

  const removeListener = useCallback((listener) => {
    listenersRef.current = listenersRef.current.filter(l => l !== listener);
  }, []);

  // Setup SSE connection on mount
  useEffect(() => {
    setupSSE();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []); // Empty array - setupSSE is available via closure

  return { addListener, removeListener };
};

export default useNotifications;
